import React, { useState } from 'react';
import { Box, Button, Typography, TextField, CircularProgress } from '@mui/material';
import { AutoAwesome as SparkIcon } from '@mui/icons-material';

const TEAL = '#206884';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * A wizard field with an AI "Give me a suggestion" affordance (create-a-card quality overhaul).
 * Mirrors the approved mockup: the button becomes "Give me 3 more" and returns 3 FRESH options each
 * click, with Previous/Next history; picking one fills the field and collapses the list to a
 * "Show suggestions" link. Cover words and imagery use SEPARATE instances — they are never merged.
 *
 * @param {'words'|'imagery'} kind  which suggestion endpoint to call
 */
const SuggestionField = ({
  kind,
  label,
  help,
  value,
  onChange,
  placeholder,
  multiline = false,
  serif = false,
  feeling = [],
  styleId,
}) => {
  const [batches, setBatches] = useState([]); // array of string[]
  const [idx, setIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [error, setError] = useState(false);

  const endpoint = kind === 'words' ? 'suggest-words' : 'suggest-imagery';
  const hasSuggestions = batches.length > 0;
  const selectedFeelings = Array.isArray(feeling)
    ? feeling.map((entry) => String(entry || '').trim()).filter(Boolean)
    : [];
  const selectedStyle = typeof styleId === 'string' ? styleId.trim() : '';
  const hasFeeling = selectedFeelings.length > 0;
  const hasStyle = !!selectedStyle;

  const loadingCopy = hasFeeling && hasStyle
    ? 'Thinking based on your feeling & style…'
    : hasFeeling
      ? 'Thinking based on your feeling…'
      : hasStyle
        ? 'Thinking based on your style…'
        : 'Thinking…';

  const provenanceCopy = (() => {
    if (!hasFeeling && !hasStyle) {
      return kind === 'words' ? 'A few kinds of cover line' : 'A few kinds of picture';
    }
    const feelingPart = hasFeeling ? `From ${selectedFeelings.join(' + ')}` : '';
    const stylePart = hasStyle ? `${hasFeeling ? 'in' : 'In'} ${selectedStyle}` : '';
    return [feelingPart, stylePart].filter(Boolean).join(' ');
  })();

  const fetchMore = async () => {
    setError(false);
    setLoading(true);
    setCollapsed(false);
    try {
      const offset = batches.reduce((n, b) => n + b.length, 0);
      const exclude = batches.reduce((acc, b) => acc.concat(b), []);
      const res = await fetch(`${API_BASE}/api/cards/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feeling, styleId: styleId || undefined, offset, exclude }),
      });
      const data = await res.json().catch(() => null);
      const suggestions = (data && data.data && data.data.suggestions) || [];
      if (!suggestions.length) throw new Error('no suggestions');
      setBatches((prev) => {
        const next = [...prev, suggestions];
        setIdx(next.length - 1);
        return next;
      });
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const pick = (text) => {
    onChange(text);
    setCollapsed(true);
  };

  const gotoBatch = (delta) => {
    const n = idx + delta;
    if (n < 0 || n >= batches.length) return;
    setIdx(n);
  };

  const currentBatch = idx >= 0 && idx < batches.length ? batches[idx] : [];

  return (
    <Box>
      <Typography component="div" sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 0.25 }}>
        {label}{' '}
        <Typography component="span" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: '0.85rem' }}>
          — optional
        </Typography>
      </Typography>
      {help && (
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1, lineHeight: 1.5 }}>
          {help}
        </Typography>
      )}

      <Button
        onClick={fetchMore}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={15} /> : <SparkIcon sx={{ fontSize: 16 }} />}
        variant="outlined"
        sx={{ textTransform: 'none', color: TEAL, borderColor: '#bcd9e0', borderRadius: 5, mb: 1 }}
      >
        {hasSuggestions ? 'Give me 3 more' : 'Give me a suggestion'}
      </Button>

      {loading && (
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 1 }}>
          {loadingCopy}
        </Typography>
      )}

      {error && !loading && (
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 1 }}>
          We couldn’t fetch suggestions just now — you can type your own below.
        </Typography>
      )}

      {hasSuggestions && !loading && collapsed && (
        <Button
          onClick={() => setCollapsed(false)}
          startIcon={<SparkIcon sx={{ fontSize: 15 }} />}
          sx={{ textTransform: 'none', color: TEAL, mb: 1 }}
        >
          Show suggestions
        </Button>
      )}

      {hasSuggestions && !loading && !collapsed && (
        <Box sx={{ mb: 1 }}>
          <Typography
            sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, mb: 1 }}
          >
            {provenanceCopy}
          </Typography>
          {currentBatch.map((text, i) => (
            <Box
              key={`${idx}-${i}`}
              component="button"
              type="button"
              onClick={() => pick(text)}
              sx={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                border: '1px solid #e0ebee',
                backgroundColor: '#f7fafb',
                borderRadius: 2,
                px: 1.75,
                py: 1.25,
                mb: 1,
                fontSize: serif ? '1.05rem' : '0.9rem',
                fontFamily: serif ? "Georgia, 'Times New Roman', serif" : 'inherit',
                color: '#33403b',
                '&:hover': { borderColor: TEAL, backgroundColor: '#eef6f8' },
              }}
            >
              {text}
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Button size="small" onClick={() => gotoBatch(-1)} disabled={idx <= 0} sx={{ textTransform: 'none', color: TEAL }}>
              ‹ Previous
            </Button>
            <Button size="small" onClick={() => gotoBatch(1)} disabled={idx >= batches.length - 1} sx={{ textTransform: 'none', color: TEAL }}>
              Next ›
            </Button>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              set {idx + 1} of {batches.length}
            </Typography>
          </Box>
        </Box>
      )}

      <TextField
        fullWidth
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        sx={{ mt: 1 }}
      />
    </Box>
  );
};

export default SuggestionField;
